
import { Database, BarChart, MessageSquare, GitMerge, Clock } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../Button";

const ClientManagementContent = () => {
  return (
    <div className="container mx-auto px-4 py-16">
      {/* Hero Section */}
      <div className="mb-16 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-6">Client Relationship Management</h1>
        <p className="text-xl md:text-2xl text-white/70 max-w-3xl mx-auto">
          AI-powered CRM systems for legal teams offer specialized features to enhance client management and streamline workflows.
        </p>
      </div>

      {/* Overview Section */}
      <div className="mb-20">
        <div className="prose prose-lg prose-invert max-w-4xl mx-auto">
          <p>
            AI-powered Customer Relationship Management (CRM) systems for legal teams offer specialized features to enhance client management and streamline workflows. The benefits include improved client satisfaction through personalized service, enhanced team productivity by reducing administrative burdens, and better decision-making with data-driven insights. These systems empower legal teams to strengthen relationships while focusing on high-value legal work.
          </p>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Key Features</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-blue-400">
              <Database size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automated Data Management</h3>
            <p className="text-white/70">
              AI updates and maintains client data by extracting information from emails, documents, and other sources, eliminating manual data entry.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-purple-400">
              <BarChart size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Client Insights</h3>
            <p className="text-white/70">
              Advanced analytics provide actionable insights into client relationships, helping legal teams identify opportunities, track engagement, and tailor outreach strategies.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-green-400">
              <MessageSquare size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Personalized Client Engagement</h3>
            <p className="text-white/70">
              AI enables customized communication by automating emails and scheduling, ensuring timely and relevant interactions.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-amber-400">
              <GitMerge size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Integrated Case Tracking</h3>
            <p className="text-white/70">
              CRM systems link client data with case details, enabling seamless access to all related documents, communications, and timelines.
            </p>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <div className="mb-4 text-red-400">
              <Clock size={36} />
            </div>
            <h3 className="text-xl font-bold mb-3">Workflow Automation</h3>
            <p className="text-white/70">
              Routine tasks like appointment scheduling, follow-ups, and document sharing are automated to improve efficiency.
            </p>
          </div>
        </div>
      </div>

      {/* Case Studies Section */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold mb-12 text-center">Curated Case Studies</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Criminal Defense Growth</h3>
            <p className="text-white/70 mb-4">
              Florida criminal defense firm grows 725% with Lawmatics
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.lawmatics.com/case-study/leppard-law/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Business Development</h3>
            <p className="text-white/70 mb-4">
              How Blank Rome uses Foundation to win more business and enable their lawyers to exceed client expectations
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://info.litera.com/blank-rome-foundation-case-study.html', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Family Law Intake</h3>
            <p className="text-white/70 mb-4">
              Accelerated intake increases profit for family law firm
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.lawmatics.com/case-study/sullivan-law-and-associates/', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Conversion Rate</h3>
            <p className="text-white/70 mb-4">
              52% conversion rate expands personal injury law firm
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://smith.ai/case-studies/ziegler-diamond-law-firm', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Tech Company Speed</h3>
            <p className="text-white/70 mb-4">
              LEXR Delivers Reliable Client Advice at the Speed of a Tech Company
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://www.deepjudge.ai/customers/lexr', '_blank')}
            >
              Read Study
            </Button>
          </div>
          
          <div className="bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-white/10 transition-colors">
            <h3 className="text-xl font-bold mb-3">Enhanced Communication</h3>
            <p className="text-white/70 mb-4">
              Law Firm enhanced client communication with Smith.ai
            </p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => window.open('https://smith.ai/case-studies/law-firm-enhanced-client-communciation', '_blank')}
            >
              Read Study
            </Button>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="text-center">
        <h2 className="text-3xl font-bold mb-6">Enhance Your Client Relationships</h2>
        <p className="text-xl text-white/70 mb-8 max-w-2xl mx-auto">
          Discover how our AI-powered CRM solutions can help your legal team build stronger client relationships.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/contact">
            <Button variant="glass" size="lg" hasAnimation={true}>Contact Us</Button>
          </Link>
          <Link to="/services/advisory">
            <Button variant="outline" size="lg">Explore Advisory Services</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ClientManagementContent;
